import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, AlignmentType, BorderStyle } from "docx";
import { Staff } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { StaffVisitDialog } from "./StaffVisitDialog";

// Define font size constants with 20% increase from the standard docx sizes
// Standard docx sizes: Heading 1 = 20pt, Heading 2 = 16pt, Heading 3 = 14pt, Normal = 11pt
const DEFAULT_FONT_SIZE = 13; // 11pt with 20% increase
const HEADING1_FONT_SIZE = 24; // 20pt with 20% increase
const HEADING2_FONT_SIZE = 19; // 16pt with 20% increase
const HEADING3_FONT_SIZE = 17; // 14pt with 20% increase

interface DepartmentVisits {
  category: string;
  visits: any[];
  totalStaff: number;
}
interface Visit {
  id: string;
  complaint: string;
  diagnosis: string;
  treatment: string;
  temperature: number;
  date: string;
  staff?: Staff;
}
export function StaffVisitsList() {
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get staff visits
  const {
    data: visits = [],
    isLoading
  } = useQuery({
    queryKey: ['daily-staff-visits', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return [];
      const {
        data,
        error
      } = await supabase.from('visits').select(`
          id,
          complaint,
          diagnosis,
          treatment,
          temperature,
          date,
          community_member:community_member_id (
            id,
            name,
            category
          )
        `).eq('school_id', user.schoolId).gte('date', today.toISOString()).lt('date', new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()).not('community_member_id', 'is', null);
      if (error) throw error;
      return data as unknown as Visit[];
    },
    enabled: !!user?.schoolId
  });
  const downloadReport = async () => {
    // Group visits by department
    const visitsByDepartment = visits.reduce<Record<string, DepartmentVisits>>((acc, visit) => {
      const category = visit.staff?.category || 'Unassigned';
      if (!acc[category]) {
        acc[category] = {
          category,
          visits: [],
          totalStaff: 0
        };
      }
      acc[category].visits.push(visit);
      acc[category].totalStaff = new Set(acc[category].visits.map(v => v.staff?.id)).size;
      return acc;
    }, {});

    // Create document sections
    const sections = [];

    // Add title and date
    sections.push(new Paragraph({
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: {
        after: 400
      },
      children: [new TextRun({
        text: "Daily Staff Clinic Report",
        bold: true,
        size: HEADING1_FONT_SIZE
      })]
    }), new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: {
        after: 400
      },
      children: [new TextRun({
        text: `Date: ${format(today, 'PPP')}`,
        size: DEFAULT_FONT_SIZE
      })]
    }), new Paragraph({
      spacing: {
        after: 400
      },
      children: [new TextRun({
        text: `Total Visits: ${visits.length}`,
        size: DEFAULT_FONT_SIZE
      })]
    }));

    // Add department summary section
    sections.push(new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: {
        before: 400,
        after: 200
      },
      children: [new TextRun({
        text: "Department Summary",
        bold: true,
        size: HEADING2_FONT_SIZE
      })]
    }), new Table({
      width: {
        size: 100,
        type: "pct"
      },
      rows: [new TableRow({
        tableHeader: true,
        children: [new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: "Department",
              bold: true,
              size: DEFAULT_FONT_SIZE
            })]
          })]
        }), new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: "Staff Visited",
              bold: true,
              size: DEFAULT_FONT_SIZE
            })]
          })]
        }), new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: "Total Visits",
              bold: true,
              size: DEFAULT_FONT_SIZE
            })]
          })]
        })]
      }), ...Object.values(visitsByDepartment).map(dept => new TableRow({
        children: [new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: dept.category.replace(/_/g, ' '),
              size: DEFAULT_FONT_SIZE
            })]
          })]
        }), new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: dept.totalStaff.toString(),
              size: DEFAULT_FONT_SIZE
            })]
          })]
        }), new TableCell({
          children: [new Paragraph({
            children: [new TextRun({
              text: dept.visits.length.toString(),
              size: DEFAULT_FONT_SIZE
            })]
          })]
        })]
      }))],
      borders: {
        top: {
          style: BorderStyle.SINGLE,
          size: 1
        },
        bottom: {
          style: BorderStyle.SINGLE,
          size: 1
        },
        left: {
          style: BorderStyle.SINGLE,
          size: 1
        },
        right: {
          style: BorderStyle.SINGLE,
          size: 1
        },
        insideHorizontal: {
          style: BorderStyle.SINGLE,
          size: 1
        },
        insideVertical: {
          style: BorderStyle.SINGLE,
          size: 1
        }
      }
    }));

    // Add detailed visits section for each department
    Object.values(visitsByDepartment).forEach(dept => {
      sections.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        spacing: {
          before: 400,
          after: 200
        },
        children: [new TextRun({
          text: `${dept.category.replace(/_/g, ' ')} Visits`,
          bold: true,
          size: HEADING3_FONT_SIZE
        })]
      }), new Table({
        width: {
          size: 100,
          type: "pct"
        },
        rows: [new TableRow({
          tableHeader: true,
          children: [new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: "Staff Name",
                bold: true,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: "Time",
                bold: true,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: "Temperature",
                bold: true,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: "Complaint",
                bold: true,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: "Diagnosis",
                bold: true,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: "Treatment",
                bold: true,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          })]
        }), ...dept.visits.map(visit => new TableRow({
          children: [new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: visit.staff?.name || 'Unknown',
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: format(new Date(visit.date), 'HH:mm'),
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: `${visit.temperature}°C`,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: visit.complaint,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: visit.diagnosis,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          }), new TableCell({
            children: [new Paragraph({
              children: [new TextRun({
                text: visit.treatment,
                size: DEFAULT_FONT_SIZE
              })]
            })]
          })]
        }))],
        borders: {
          top: {
            style: BorderStyle.SINGLE,
            size: 1
          },
          bottom: {
            style: BorderStyle.SINGLE,
            size: 1
          },
          left: {
            style: BorderStyle.SINGLE,
            size: 1
          },
          right: {
            style: BorderStyle.SINGLE,
            size: 1
          },
          insideHorizontal: {
            style: BorderStyle.SINGLE,
            size: 1
          },
          insideVertical: {
            style: BorderStyle.SINGLE,
            size: 1
          }
        }
      }));
    });

    // Create and save the document
    const doc = new Document({
      sections: [{
        properties: {},
        children: sections
      }]
    });
    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `staff_clinic_report_${format(today, 'yyyy-MM-dd')}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  const formatReportData = () => {
    // Group visits by department
    return visits.reduce<Record<string, DepartmentVisits>>((acc, visit) => {
      const category = visit.staff?.category || 'Unassigned';
      if (!acc[category]) {
        acc[category] = {
          category,
          visits: [],
          totalStaff: 0
        };
      }
      acc[category].visits.push(visit);
      acc[category].totalStaff = new Set(acc[category].visits.map(v => v.staff?.id)).size;
      return acc;
    }, {});
  };
  const handleTreatAgain = (staff: Staff) => {
    setSelectedStaff(staff);
    setStaffDialogOpen(true);
  };
  if (isLoading) {
    return <div>Loading visits...</div>;
  }
  return <div className="space-y-6">
      

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visits.map(visit => <Card key={visit.id}>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="text-xl font-bold">
                  {format(new Date(visit.date), 'HH:mm')}
                </div>
                
                <div className="space-y-2">
                  <div className="grid grid-cols-[auto,1fr] gap-x-2">
                    <span className="font-medium">Staff:</span>
                    <span>{visit.staff?.name}</span>
                    
                    <span className="font-medium">Department:</span>
                    <span>{visit.staff?.category.replace(/_/g, ' ')}</span>
                    
                    <span className="font-medium">Temperature:</span>
                    <span>{visit.temperature}°C</span>
                    
                    <span className="font-medium">Complaint:</span>
                    <span>{visit.complaint}</span>
                    
                    <span className="font-medium">Diagnosis:</span>
                    <span>{visit.diagnosis}</span>
                    
                    <span className="font-medium">Treatment:</span>
                    <span>{visit.treatment}</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => visit.staff && handleTreatAgain(visit.staff)}>
                    Treat Again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>)}
      </div>

      <Dialog open={isReportOpen} onOpenChange={setIsReportOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Staff Clinic Report - {format(today, 'PPP')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-120px)] pr-4">
            <div className="space-y-6 p-4">
              <div className="text-center space-y-2">
                <h1 className="text-2xl font-bold">Staff Clinic Report</h1>
                <p>{format(today, 'PPP')}</p>
                <p>Total Visits: {visits.length}</p>
              </div>

              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Department Summary</h2>
                <div className="border rounded-lg">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left">Department</th>
                        <th className="px-4 py-2 text-left">Staff Visited</th>
                        <th className="px-4 py-2 text-left">Total Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(formatReportData()).map(([category, data]) => <tr key={category} className="border-b">
                          <td className="px-4 py-2">{category.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2">{data.totalStaff}</td>
                          <td className="px-4 py-2">{data.visits.length}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Detailed Visit Records</h2>
                {Object.entries(formatReportData()).map(([category, data]) => <div key={category} className="space-y-4">
                    <h3 className="text-lg font-medium">{category.replace(/_/g, ' ')} Visits</h3>
                    <div className="border rounded-lg">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="px-4 py-2 text-left">Staff Name</th>
                            <th className="px-4 py-2 text-left">Time</th>
                            <th className="px-4 py-2 text-left">Temperature</th>
                            <th className="px-4 py-2 text-left">Complaint</th>
                            <th className="px-4 py-2 text-left">Diagnosis</th>
                            <th className="px-4 py-2 text-left">Treatment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.visits.map(visit => <tr key={visit.id} className="border-b">
                              <td className="px-4 py-2">{visit.staff?.name}</td>
                              <td className="px-4 py-2">
                                {format(new Date(visit.date), 'HH:mm')}
                              </td>
                              <td className="px-4 py-2">{visit.temperature}°C</td>
                              <td className="px-4 py-2">{visit.complaint}</td>
                              <td className="px-4 py-2">{visit.diagnosis}</td>
                              <td className="px-4 py-2">{visit.treatment}</td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>)}
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <StaffVisitDialog staff={selectedStaff} open={staffDialogOpen} onOpenChange={setStaffDialogOpen} />
    </div>;
}